import { css } from '@emotion/css';

/**
 * Gap between columns.
 *
 * Wide enough to hold both a drag handle and the resize divider without them
 * fighting for the same pixels: the drag handle for a block renders at
 * `blockLeft - (DRAG_HANDLE_CONTAINER_WIDTH + offset)`, i.e. roughly
 * [blockLeft - 18, blockLeft - 2], so the divider is parked at the far left
 * of the gutter, clear of it.
 */
export const COLUMN_GAP = 28;

/** Width of the divider's grab area, at the left end of the gutter. */
const DIVIDER_HIT_WIDTH = 10;

/**
 * Smallest share of the row a column may be shrunk to by dragging a divider,
 * as a fraction of the row's total ratio.
 */
export const MIN_COLUMN_RATIO = 0.15;

/** Below this viewport width the row scrolls sideways instead of squeezing. */
export const COLUMNS_STACK_BREAKPOINT = 640;

const narrow = `@media (max-width: ${COLUMNS_STACK_BREAKPOINT}px)`;

export const columnsHostStyles = css({
  display: 'block',
  margin: '12px 0',
});

export const columnsContainerStyles = css({
  display: 'flex',
  gap: `${COLUMN_GAP}px`,
  alignItems: 'flex-start',
  width: '100%',
  [narrow]: {
    overflowX: 'auto',
    // Keep the sideways scroll inside the row so it does not hand off to the
    // page's vertical scroll on touch.
    overscrollBehaviorX: 'contain',
    scrollbarWidth: 'thin',
  },
});

export const columnHostStyles = css({
  display: 'block',
  position: 'relative',
  minWidth: 0,
  [narrow]: {
    // Stop flex-shrink from crushing columns; the row scrolls instead.
    minWidth: '240px',
    flexShrink: 0,
  },
});

export const columnDividerStyles = css({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: `${-COLUMN_GAP}px`,
  width: `${DIVIDER_HIT_WIDTH}px`,
  cursor: 'col-resize',
  // Below the drag-handle overlay on purpose. Where the two still overlap —
  // a hovered block with a toggle arrow pushes its handle further left — the
  // handle wins, and the divider stays grabbable above and below it.
  zIndex: 0,
  touchAction: 'none',
  [narrow]: {
    display: 'none',
  },
});

export const columnDividerLineStyles = css({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: '50%',
  width: '2px',
  transform: 'translateX(-50%)',
  borderRadius: '1px',
  background: 'transparent',
  transition: 'background 0.15s ease',
  '.affine-column-divider:hover &, .affine-column-divider[data-dragging] &': {
    background: 'var(--affine-primary-color, #1e96eb)',
  },
});
